import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@devhop/ui";
import { navigateFromActionUrl } from "@/shared/lib/notification-navigation";
import { useNavigate } from "@tanstack/react-router";
import { BellIcon } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { enUS } from "date-fns/locale";
import {
  useMarkAllNotificationsReadMutation,
  useMarkNotificationReadMutation,
  useNotificationsQuery,
  useUnreadNotificationsCountQuery,
} from "../api/queries";

export function NotificationBell() {
  const nav = useNavigate({ from: "/app" });
  const { data: countData } = useUnreadNotificationsCountQuery();
  const { data: listData, isLoading } = useNotificationsQuery({
    limit: 10,
    offset: 0,
  });
  const markRead = useMarkNotificationReadMutation();
  const markAllRead = useMarkAllNotificationsReadMutation();

  const unreadCount = countData?.count ?? 0;
  const items = listData?.items ?? [];

  const handleOpen = (id: string, actionUrl: string | null, readAt: string | null) => {
    if (!readAt) markRead.mutate(id);
    if (actionUrl) navigateFromActionUrl(actionUrl, nav);
  };

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative size-8">
          <BellIcon className="size-4" />
          {unreadCount > 0 ? (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 flex size-4 items-center justify-center rounded-full p-0 text-[10px]"
            >
              {unreadCount > 9 ? "9+" : unreadCount}
            </Badge>
          ) : null}
          <span className="sr-only">ການແຈ້ງເຕືອນ</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-80" align="end" forceMount>
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>ການແຈ້ງເຕືອນ</span>
          {unreadCount > 0 ? (
            <Button
              variant="ghost"
              size="sm"
              className="h-auto px-2 py-1 text-xs"
              disabled={markAllRead.isPending}
              onClick={(e) => {
                e.preventDefault();
                markAllRead.mutate();
              }}
            >
              ອ່ານທັງໝົດ
            </Button>
          ) : null}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {isLoading ? (
          <div className="px-3 py-6 text-center text-muted-foreground text-sm">
            ກຳລັງໂຫຼດ...
          </div>
        ) : items.length === 0 ? (
          <div className="px-3 py-6 text-center text-muted-foreground text-sm">
            ບໍ່ມີການແຈ້ງເຕືອນ
          </div>
        ) : (
          items.map((item) => (
            <DropdownMenuItem
              key={item.id}
              className="flex cursor-pointer flex-col items-start gap-1 py-2"
              onClick={() => handleOpen(item.id, item.actionUrl, item.readAt)}
            >
              <div className="flex w-full items-start justify-between gap-2">
                <span
                  className={
                    item.readAt
                      ? "font-medium text-sm"
                      : "font-semibold text-sm"
                  }
                >
                  {item.title}
                </span>
                {!item.readAt ? (
                  <span className="mt-1 size-2 shrink-0 rounded-full bg-primary" />
                ) : null}
              </div>
              {item.body ? (
                <span className="line-clamp-2 text-muted-foreground text-xs">
                  {item.body}
                </span>
              ) : null}
              <span className="text-muted-foreground text-[10px]">
                {formatDistanceToNow(new Date(item.createdAt), {
                  addSuffix: true,
                  locale: enUS,
                })}
              </span>
            </DropdownMenuItem>
          ))
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="justify-center text-center text-sm"
          onClick={() => nav({ to: "/app/notifications" })}
        >
          ເບິ່ງທັງໝົດ
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
